import {Placement} from '@popperjs/core';
import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../helpers/src/browser/keyboardHelpers';
import {buildInteractionIdProps} from '../../../../helpers/src/interaction/interactionHelpers';
import {InteractionParent} from '../../interactionTracking/interactionParent';
import {shadows} from '../../styles/colorStyles';
import {KeyboardShortcuts} from '../keyboard/keyboardShortcuts';
import {RepositionPopover, RepositionPopoverProps} from '../popovers/repositionPopover';

/*
 * Props.
 */

export interface DropdownProps extends RepositionPopoverProps {
  width?: number;
  height?: number;
  maxWidth?: number;
  maxHeight?: number;
  interactionId?: string;
  className?: string;
  isExclusive?: boolean;
}

/*
 * Style.
 */

export interface DropdownStyleProps {
  $width?: number;
  $height?: number;
  $maxWidth?: number;
  $maxHeight?: number;
  $placement?: Placement;
}

const StyledDropdownDiv = styled.div<DropdownStyleProps>`
  display: flex;
  flex-direction: column;

  overflow: hidden;
  border-radius: 8px;
  background-color: ${(p) => p.theme.foreground};
  color: ${(p) => p.theme.text};
  ${shadows.dialogsAndDropdowns};

  ${(p) => getSideMargin(p.$placement)}

  width: ${(p) => p.$width && `${p.$width}px`};
  height: ${(p) => p.$height && `${p.$height}px`};
  max-width: ${(p) => p.$maxWidth && `${p.$maxWidth}px`};
  max-height: ${(p) => p.$maxHeight && `${p.$maxHeight}px`};
`;

const getSideMargin = (placement?: Placement) => {
  const defaultMargin = 'margin: 5px 0;';
  if (!placement) {
    return defaultMargin;
  }

  if (placement.indexOf('right') !== -1) {
    return 'margin-left: 5px;';
  }

  if (placement.indexOf('left') !== -1) {
    return 'margin-right: 5px;';
  }

  return defaultMargin;
};

/*
 * Component.
 */

export const Dropdown: FC<PropsWithChildren<DropdownProps>> = (props) => {
  const {onRequestClose, isExclusive = true} = props;
  const keyHandlers = {
    [KeyboardActionsEnum.CLOSE_LEGACY]: () => {
      if (onRequestClose) {
        onRequestClose();
      }
    },
  };

  const interactionId = props.interactionId || 'dropdown';
  const interactionProps = buildInteractionIdProps(interactionId);

  return (
    <KeyboardShortcuts handlers={keyHandlers} requestExclusivity>
      <InteractionParent interactionId={interactionId}>
        <RepositionPopover isExclusive={isExclusive} {...props}>
          <StyledDropdownDiv
            $width={props.width}
            $height={props.height}
            $maxWidth={props.maxWidth}
            $maxHeight={props.maxHeight}
            $placement={props.placement}
            className={props.className}
            {...interactionProps}
          >
            {props.children}
          </StyledDropdownDiv>
        </RepositionPopover>
      </InteractionParent>
    </KeyboardShortcuts>
  );
};
